import './requestReassignPopup.scss';

import { Guid } from '@approvalmax/types';
import { Popup, Radiobox, SearchInput } from '@approvalmax/ui';
import { selectors, ui } from 'modules/common';
import { State } from 'modules/data';
import { useShallowEqualSelector } from 'modules/react-redux';
import { LockIcon } from 'modules/sprites';
import { FC, memo, useMemo, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';

import { reassignRequest } from '../actions';
import { RequestReassignPopupData } from '../reducers/page/activePopup/requestReassignPopupReducer';
import { getActivePopup, isActiveRequestLocked } from '../selectors/pageSelectors';
import { messages } from './RequestReassignPopup.messages';

const bem = bemFactory.block('reql-request-reassign-popup');
const qa = bemFactory.qa('reql-request-reassign-popup');

const RequestReassignPopup: FC = () => {
    const [selectedUserId, setSelectedUserId] = useState<Guid | null>(null);
    const [filter, setFilter] = useState('');
    const [commentText, setCommentText] = useState('');
    const dispatch = useDispatch();
    const { request, step, isLocked, team, isReviewStep } = useShallowEqualSelector((state: State) => {
        const { requestId, stepId } = getActivePopup<RequestReassignPopupData>(state);
        const request = selectors.request.getRequestById(state, requestId);
        const isReviewStep = request.reviewStep?.reviewStepId === stepId;
        const step =
            request.reviewStep?.reviewStepId === stepId
                ? request.reviewStep
                : request.steps.find((s) => s.id === stepId)!;

        return {
            request,
            step,
            isLocked: isActiveRequestLocked(state),
            team: selectors.company.getCompanyTeam(state, request.company),
            isReviewStep,
        };
    });

    const onDone = () => {
        dispatch(
            reassignRequest({
                requestId: request.id,
                step: step,
                target: team.find((t) => t.id === selectedUserId)!,
                commentText: commentText,
                companyId: request.companyId,
            })
        );
    };

    const selectedUser = selectedUserId ? team.find((u) => u.id === selectedUserId) : null;

    const participants = useMemo(
        () =>
            team
                .filter((user) => (filter ? user.displayName.toLowerCase().includes(filter.toLowerCase()) : true))
                .map((user) => {
                    const participants = 'participants' in step ? step.participants : step?.reviewers;

                    const locked = participants.some((p) => ('userId' in p ? p.userId === user.id : p.id === user.id));
                    const selected = user.id === selectedUserId;
                    const lockedText = isReviewStep
                        ? messages.cannotSelectReviewer({
                              displayName: user.displayName,
                          })
                        : messages.cannotSelectApprover({
                              displayName: user.displayName,
                          });

                    return (
                        <div
                            key={user.id}
                            className={bem('participant-item', { locked, selected })}
                            data-qa={qa('participant-item')}
                            data-qa-id={user.id}
                            data-qa-name={user.displayName}
                            onClick={!locked ? () => setSelectedUserId(user.id) : undefined}
                            title={locked ? lockedText : user.displayName}
                        >
                            <div className={bem('participant-item-radio-wrp')}>
                                <Radiobox
                                    className={bem('participant-item-radio')}
                                    checked={selected}
                                    disabled={locked}
                                />
                            </div>

                            <ui.Avatar title={null} size={30} user={user} className={bem('participant-avatar')} />

                            <div className={bem('participant-name')}>{user.displayName}</div>

                            {locked && <LockIcon className={bem('participant-lock-icon')} width={8} height={10} />}
                        </div>
                    );
                }),
        [filter, isReviewStep, selectedUserId, step, team]
    );

    return (
        <Popup.DefaultContent
            className={bem.add('fs-mask')()}
            qa={qa()}
            title={messages.popupTitle}
            buttons={
                <Popup.DefaultContent.Button
                    disabled={isLocked || !selectedUser}
                    qa={qa('done-button')}
                    execute={onDone}
                    title={
                        selectedUser
                            ? messages.doneButtonTitleWithName({
                                  displayName: selectedUser.displayName,
                              })
                            : messages.doneButtonTitle
                    }
                >
                    {messages.doneButtonText}
                </Popup.DefaultContent.Button>
            }
        >
            <div className={bem('content')}>
                <div className={bem('description-block')}>
                    {isReviewStep
                        ? messages.descriptionReviewer({
                              authorName: request.author.displayName,
                              br: <br />,
                          })
                        : messages.descriptionApprover({
                              authorName: request.author.displayName,
                              br: <br />,
                          })}
                </div>

                <div className={bem('search-block')}>
                    <SearchInput
                        focusOnMount
                        qa={qa('search-input')}
                        theme='transparent'
                        value={filter}
                        onChange={setFilter}
                    />
                </div>

                <div className={bem('participant-list-block')} data-qa={qa('participant-list')}>
                    {participants}
                </div>

                <div className={bem('comment-editor-block')}>
                    <ui.AddCommentEditor
                        className={bem('comment-editor')}
                        qa={qa('comment-editor')}
                        value={commentText}
                        onChange={setCommentText}
                    />
                </div>
            </div>
        </Popup.DefaultContent>
    );
};

export default memo(RequestReassignPopup);
